import { Card, Stack, Table, Badge, Loader, Center, Button, Group, ActionIcon } from "@mantine/core";
import { IconPlus, IconEdit, IconTrash, IconPrinter } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import { useResource, useRemove } from "../../../shared/api";
import { PageHeader, EmptyState, ResourceForm, confirmDelete, type FormField } from "../../../shared/ui";

export function KdsStationsPage() {
  const { data, isLoading } = useResource<any>("kds-stations");
  // PRINTER tipindeki cihazları çek — istasyon yazıcısı select için
  const { data: devicesData } = useResource<any>("hardware-devices", {
    filters: { device_type: "PRINTER" },
  });
  const remove = useRemove("kds-stations");
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const printers: any[] = devicesData?.results || devicesData || [];
  const fields: FormField[] = useMemo(() => [
    { name: "code", label: "Kod", required: true, placeholder: "GRILL, PIZZA, BAR..." },
    { name: "name", label: "Ad", required: true },
    { name: "warn_after_seconds", label: "Geç Uyarı (saniye)", type: "number", defaultValue: 300, min: 60 },
    {
      name: "printer", label: "Mutfak Yazıcısı", type: "select",
      clearable: true,
      description: "Seçilirse onayda otomatik mutfak fişi bu yazıcıya basılır. Boş = bu istasyon fiş basmaz.",
      options: printers.map((p) => ({
        value: p.id,
        label: p.name + (p.branch_name ? ` (${p.branch_name})` : ""),
      })),
    },
    { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
  ], [printers]);

  const list: any[] = data?.results || data || [];
  if (isLoading) return <Center h={300}><Loader /></Center>;

  async function handleDelete(id: string) {
    if (!(await confirmDelete("İstasyon silinsin mi?"))) return;
    try {
      await remove.mutateAsync(id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Mutfak İstasyonları"
        subtitle={`${list.length} istasyon`}
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={() => { setEditing(null); open(); }}>
            Yeni İstasyon
          </Button>
        }
      />

      {list.length === 0 ? (
        <EmptyState
          title="İstasyon yok"
          description="GRILL / PIZZA / BAR gibi mutfak istasyonları ekleyin; ürünler bunlara route edilir."
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Table.ScrollContainer minWidth={560}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kod</Table.Th>
                <Table.Th>Ad</Table.Th>
                <Table.Th>Yazıcı</Table.Th>
                <Table.Th>Geç Uyarı</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((s: any) => (
                <Table.Tr key={s.id}>
                  <Table.Td><Badge variant="filled">{s.code}</Badge></Table.Td>
                  <Table.Td fw={500}>{s.name}</Table.Td>
                  <Table.Td>
                    {s.printer_name ? (
                      <Badge variant="light" leftSection={<IconPrinter size={12} />}>
                        {s.printer_name}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">Yok (fiş basmaz)</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>{s.warn_after_seconds || 300} sn</Table.Td>
                  <Table.Td>{s.is_active ? <Badge color="green">Aktif</Badge> : <Badge color="gray">Pasif</Badge>}</Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => { setEditing(s); open(); }}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(s.id)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="kds-stations"
        title={editing ? "İstasyon Düzenle" : "Yeni İstasyon"}
        fields={fields}
        editId={editing?.id || null}
        initialValues={editing || undefined}
      />
    </Stack>
  );
}
